import { Chat } from '@/network/RestApi';
import { useChatStore, type ChatChannel } from '@/state/chatStore';
import { useCharacterStore } from '@/state/characterStore';
import { useAuthStore } from '@/state/authStore';
import { getPitayaClient } from '@/network/PitayaClient';

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:8080';

function wsBase(): string {
  const url = new URL(API_BASE);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString().replace(/\/$/, '');
}

export type NearbyChatMsg = { uid: string; from: string; text: string };
type NearbyChatListener = (msg: NearbyChatMsg) => void;

export class ChatSystem {
  private ws?: WebSocket;
  private nearbyUnsub?: () => void;
  private nearbyListeners = new Set<NearbyChatListener>();
  private currentRoom = 'town';

  private localUid = '';

  setRoom(room: string) { this.currentRoom = room; }
  setLocalUid(uid: string) { this.localUid = uid; }

  bindWebSocket() {
    this.unbind();
    const ch = useCharacterStore.getState().current;
    const token = useAuthStore.getState().accessToken;
    if (!ch || !token) return;

    const url = `${wsBase()}/api/chat/ws/${ch.id}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data as string) as {
          channel: string; senderName: string; senderId: number; body: string;
        };
        if (msg.senderId === ch.id) return;
        useChatStore.getState().push({ channel: msg.channel as ChatChannel, from: msg.senderName, text: msg.body });
      } catch { /* ignore malformed */ }
    };
    ws.onclose = () => { this.ws = undefined; };

    const cli = getPitayaClient();
    if (cli) {
      this.nearbyUnsub = cli.on('nearby_chat', (data) => {
        const m = data as NearbyChatMsg;
        const isSelf = m.uid === this.localUid;
        if (!isSelf) {
          useChatStore.getState().push({ channel: 'nearby', from: m.from, text: m.text });
        }
        this.nearbyListeners.forEach((fn) => fn(m));
      });
    }
  }

  unbind() {
    this.ws?.close();
    this.ws = undefined;
    this.nearbyUnsub?.();
    this.nearbyUnsub = undefined;
  }

  onNearbyChat(fn: NearbyChatListener): () => void {
    this.nearbyListeners.add(fn);
    return () => this.nearbyListeners.delete(fn);
  }

  async send(channel: ChatChannel, text: string) {
    const ch = useCharacterStore.getState().current;
    if (!ch || channel === 'system') return;

    if (channel === 'nearby') {
      const cli = getPitayaClient();
      if (!cli) return;
      cli.notify(`${this.currentRoom}.nearbychat`, { text });
      useChatStore.getState().push({ channel: 'nearby', from: ch.name, text });
      return;
    }

    await Chat.send({ channel, characterId: ch.id, body: text });
    useChatStore.getState().push({ channel, from: ch.name, text });
  }
}

export const chat = new ChatSystem();
